import { useState, useMemo } from 'react';
import { useUserExecutions } from '../../features/projects/hooks/useUserExecutions';
import { WorkPillExecution } from '../../types/navigationTypes';
import { normalizeText } from '../../../lib/utils';

export interface UseProjectSelectorReturn {
  // Estado
  selectedProject: WorkPillExecution | undefined;
  isModalOpen: boolean;
  
  // Acciones
  openModal: () => void;
  closeModal: () => void;
  selectProject: (executionId: string) => void;
  refreshProjects: () => Promise<void>;
  updateProjectName: (executionId: string, newName: string) => Promise<boolean>;
  
  // Datos
  projects: WorkPillExecution[];
  isLoading: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedWorkPill: string;
  setSelectedWorkPill: (workPill: string) => void;
  sortBy: 'newest' | 'oldest';
  setSortBy: (sort: 'newest' | 'oldest') => void;
  filteredProjects: WorkPillExecution[];
  uniqueWorkPills: string[];
}

export function useProjectSelector(externalSelectedExecutionId?: string): UseProjectSelectorReturn {
  const { 
    executions, 
    loading, 
    selectedExecutionId, 
    selectExecution, 
    refreshExecutions,
    updateExecutionName
  } = useUserExecutions();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedWorkPill, setSelectedWorkPill] = useState("all");
  const [sortBy, setSortBy] = useState<'newest' | 'oldest'>('newest');

  const selectedProject = useMemo(() => {
    const id = externalSelectedExecutionId || selectedExecutionId;
    return executions.find(ex => ex.id === id);
  }, [executions, selectedExecutionId, externalSelectedExecutionId]);

  const uniqueWorkPills = useMemo(() => {
    const workpills = new Set<string>();
    if (executions && executions.length > 0) {
      executions.forEach(ex => {
        // Verificar si workpill existe y tiene nombre
        if (ex.workpill?.name) {
          workpills.add(ex.workpill.name);
        }
      });
    }
    return Array.from(workpills).sort();
  }, [executions]);

  const filteredProjects = useMemo(() => {
    if (!executions) return [];

    // Primero filtramos
    const filtered = executions.filter(ex => {
      // Filtro por término de búsqueda (insensible a mayúsculas y acentos)
      const normalizedSearchTerm = normalizeText(searchTerm);
      const normalizedProjectName = normalizeText(ex.name);
      const matchesSearch = normalizedProjectName.includes(normalizedSearchTerm);
      
      // Filtro por WorkPill
      const matchesWorkPill = selectedWorkPill === "all" || 
                             (ex.workpill?.name === selectedWorkPill);

      return matchesSearch && matchesWorkPill;
    });

    // Luego ordenamos por fecha
    return filtered.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
    });
  }, [executions, searchTerm, selectedWorkPill, sortBy]);

  const handleSelectProject = (executionId: string) => {
    selectExecution(executionId);
    setIsModalOpen(false);
  };

  return {
    selectedProject,
    isModalOpen,
    openModal: () => setIsModalOpen(true),
    closeModal: () => setIsModalOpen(false),
    selectProject: handleSelectProject,
    refreshProjects: refreshExecutions,
    updateProjectName: updateExecutionName,
    projects: executions,
    isLoading: loading,
    searchTerm,
    setSearchTerm,
    selectedWorkPill,
    setSelectedWorkPill,
    sortBy,
    setSortBy,
    filteredProjects,
    uniqueWorkPills
  };
}
